import { UserManager } from 'oidc-client'
import { isEmpty, isNil } from 'ramda'
import config from 'config'
import { refreshAccessToken } from './refreshAccessToken'
import InMemoryStateStore from './inMemoryStore'

const bindUserStore = () => {
    try {
        return new InMemoryStateStore()
    } catch (err) {
        return {}
    }
}

const defaultUserManagerConfig = {
    redirect_uri: `${window.location.origin}/callback`,
    silent_redirect_uri: `${window.location.origin}/renew.html`,
    post_logout_redirect_uri: `${window.location.origin}${window.config.APP_SUBPATH}`,
    response_type: 'code',
    scope: 'openid profile email',
    filterProtocolClaims: true,
    loadUserInfo: false,
    automaticSilentRenew: false,
    accessTokenExpiringNotificationTime: 60,
    userStore: bindUserStore(),
    monitorSession: true,
}
const authSettings = {
    authority: config.AUTHORITY_URL,
    client_id: config.CLIENT_ID,
    scope: config.SCOPE,
    response_type: config.RESPONSE_TYPE ?? 'code',
}

const userManager = new UserManager({
    ...defaultUserManagerConfig,
    ...authSettings,
})

// Track if tokens have been revoked to prevent duplicate API calls
let tokensRevokedFlag = false

/**
 * Revokes both access and refresh tokens for the current user and clears session storage
 * This function is idempotent - safe to call multiple times
 * This function is used by both manual logout and silent logout scenarios
 * @returns {Promise<void>}
 */
const revokeUserTokens = async () => {
    // Prevent duplicate revocation attempts
    if (tokensRevokedFlag) {
        return
    }

    tokensRevokedFlag = true

    // Clear master password from session storage
    sessionStorage.removeItem('MasterPassword')

    try {
        const user = await userManager.getUser()

        if (!user) {
            return
        }

        // Revoke access token
        if (user.access_token) {
            try {
                await userManager._revokeToken(
                    user.access_token,
                    'access_token',
                )
            } catch (error) {
                console.error('Error revoking access token:', error)
            }
        }

        // Revoke refresh token
        if (user.refresh_token) {
            try {
                await userManager._revokeToken(
                    user.refresh_token,
                    'refresh_token',
                )
            } catch (error) {
                console.error('Error revoking refresh token:', error)
            }
        }
    } catch (error) {
        console.error('Error during token revocation:', error)
    }
}

const userSignedOutEventHandler = async () => {
    // Revoke tokens when session ends outside the app (check session iframe detection)
    await revokeUserTokens()

    // Always redirect to signout page, even if token revocation fails
    window.location.replace(`${window.location.origin}/signOut`)
}

const accessTokenExpiringEventHandler = async () => {
    if (
        !(window.location.pathname === config.APP_SUBPATH + '/sessionExpired')
    ) {
        refreshAccessToken(userManager)
    }
}

userManager.events.addUserSignedOut(userSignedOutEventHandler)
userManager.events.addAccessTokenExpiring(() => {
    accessTokenExpiringEventHandler()
})
userManager._revokeToken = async function (
    token,
    tokenTypeHint = 'access_token',
) {
    if (!token) {
        return Promise.resolve(false)
    }
    var user = await this.getUser()
    let revocationEndpoint =
        await this.settings.metadataService.getRevocationEndpoint()

    if (!revocationEndpoint) {
        revocationEndpoint =
            await this.settings.metadataService._getMetadataProperty(
                'tokenrevoke_endpoint',
            )
    }
    if (!revocationEndpoint) {
        return Promise.reject(
            new Error('Revocation endpoint not found in metadata'),
        )
    }

    const params = new URLSearchParams()
    params.append('token', token)
    params.append('token_type_hint', tokenTypeHint)
    params.append('client_id', config.CLIENT_ID)

    return fetch(revocationEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${user?.access_token}`,
        },
        body: params,
    }).then((response) => {
        if (!response.ok) {
            return response.text().then((text) => {
                throw new Error(`Revocation failed: ${response.status} ${text}`)
            })
        }
        return true
    })
}

const getUserManager = () => {
    if (isEmpty(authSettings.client_id) || isNil(authSettings.client_id)) {
        throw new Error(
            'Client ID (prop: "client_id") is not provided while setting up AuthContextProvider.',
        )
    }
    if (isEmpty(authSettings.authority) || isNil(authSettings.authority)) {
        throw new Error(
            'Authority (prop: "authority") is not provided while setting up AuthContextProvider.',
        )
    }
    return userManager
}

export default getUserManager
export { revokeUserTokens }
